import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { dehydrate, DehydratedState, QueryClient } from '@tanstack/react-query';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { Fragment } from 'react';

import { PageHead } from '_components/base/head';
import { PublicGoogleTagManager } from '_components/common';
import { LayoutPagesRouter } from '_components/layout/LayoutPagesRouter';
import { PageSeoTag } from '_components/SEO';
import { cacheConfig } from '_config/cache.config';
import { extractDomainRelevantInfoFromHeaders } from '_config/domain.config';
import { AppProviders } from '_core/app-providers';
import { PublicAppContextProvider } from '_core/context/public-app-context';
import { PublicApiRepo } from '_core/repos/public-api.repo';
import { baseSsrPrefetchQueries } from '_core/utils/public-api';
import { getServerSideTranslations } from '_core/utils/ssr';
import { projectConfig } from '_features/project/config';
import { NewProjectPage } from '_features/project/new';

type Props = {
  canonical: string;
  origin: string;
  locale: string;
  publicApiNavBarUrl: string;
  expertise?: number;
  dehydratedState: DehydratedState;
};

export default function PageRoute(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <AppProviders dehydratedState={props.dehydratedState}>
      <Route {...props} />
    </AppProviders>
  );
}

const Route = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { canonical, origin, locale, publicApiNavBarUrl, expertise } = props;
  const { t } = useTranslation(projectConfig.i18nNamespaces);
  PublicApiRepo.publicApiNavBarUrl = publicApiNavBarUrl;

  return (
    <Fragment>
      <PageHead hasReviewWidget={true} />
      <PublicAppContextProvider
        canonical={canonical}
        origin={origin}
        locale={locale || 'en'}
        briefingOptions={{ page: 'new', expertise }}>
        <PublicGoogleTagManager />

        <PageSeoTag
          title={t('project:new.seo.title')}
          description={t('project:new.seo.description')}
          nextSeoProps={{ nofollow: true, noindex: true }}
          canonical={canonical}
        />
        <LayoutPagesRouter>{<NewProjectPage />}</LayoutPagesRouter>
      </PublicAppContextProvider>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const { expertiseId } = context.query;
  const { canonical, origin, locale } = extractDomainRelevantInfoFromHeaders(context.req);

  const queryClient = new QueryClient();
  let publicApiNavBarUrl = '';

  try {
    const response = await baseSsrPrefetchQueries(origin, locale, context.resolvedUrl, queryClient);
    publicApiNavBarUrl = response.publicApiNavBarUrl;
  } catch (e) {
    // Limitation: Typescript exceptions are not typed, so we rely on what is supposed to happen
    if ((e as any).status !== 404) {
      throw e;
    }
  }

  const cacheOptions = cacheConfig.pages['staticPages'];
  if (!context.res.headersSent && cacheOptions != null) {
    context.res.setHeader('Cache-Control', cacheOptions.httpCacheControl);
  }

  const expertise = expertiseId && expertiseId !== '' ? parseInt(expertiseId as string) : undefined;

  const { i18nNamespaces } = projectConfig;
  return {
    props: {
      canonical,
      origin,
      locale,
      publicApiNavBarUrl,
      dehydratedState: dehydrate(queryClient),
      ...{ ...(expertise ? { expertise } : {}) },
      ...(await getServerSideTranslations(locale, i18nNamespaces)),
    },
  };
};
